import type { AttachPreview, DeploymentCard } from '@central/api';
import { color, font } from '@central/ui';
import {
  Alert,
  Anchor,
  Checkbox,
  Loader,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { attachPreviewQuery } from '../api';
import { errorMessage, fileSize, healthLabel } from '../format';
import type { AttachChoice } from './attachments';

const unavailable: Record<string, string> = {
  logs_not_visible: 'Logs aren’t shown for this deployment.',
  logs_not_shared: 'Logs aren’t shared with your role in this organization.',
  logs_unavailable: 'Logs can’t be read right now. Try again in a few minutes.',
  not_visible: 'This deployment doesn’t share service detail.',
};

/** How long the services choice must stay put before the logs are read again. */
const SERVICES_SETTLE_MS = 400;

const box = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  padding: '10px 12px',
  border: `1px solid ${color.border}`,
  borderRadius: 8,
  fontSize: 14,
} as const;

function listPhrase(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

/**
 * "Attach from <deployment>" on a new message
 * (docs/design/CustomerMessages.dc.html): the last hour of logs and a health
 * snapshot, pulled by Central when the message is sent, beside the
 * customer's own files. Each is offered only when the deployment can share
 * it: never for a shared deployment, and logs only where the portal's log
 * search would show them. Choosing a dedicated deployment reads only
 * whether each can be attached; the logs themselves, an audited log query,
 * are read once Logs is ticked or the preview is opened, and again when the
 * chosen services settle.
 */
export function AttachFromDeployment({
  deployment,
  value,
  onChange,
  files,
}: {
  deployment?: DeploymentCard;
  value: AttachChoice;
  onChange: (v: AttachChoice) => void;
  files: ReactNode;
}) {
  const [previewing, setPreviewing] = useState(false);
  const dedicated = deployment?.kind === 'dedicated';
  const withLogs = value.logs || previewing;
  const [settledServices] = useDebouncedValue(value.services, SERVICES_SETTLE_MS);
  const preview = useQuery({
    ...attachPreviewQuery(deployment?.id ?? '', settledServices, withLogs),
    enabled: dedicated,
    // Keeps the last answer for this deployment while the next one loads.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === deployment?.id ? keepPreviousData(prev) : undefined,
  });
  const p = preview.data;
  const logsGone = value.logs && !!p && !p.logs.available;
  useEffect(() => {
    if (logsGone) onChange({ ...value, logs: false });
  }, [logsGone, onChange, value]);
  const logsOk = dedicated && !!p?.logs.available;
  const healthOk = dedicated && !!p?.health.available;
  const services = value.services.length ? listPhrase(value.services) : 'all services';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: color.textBody }}>
        {deployment ? `Attach from ${deployment.name}` : 'Attach'}
      </span>
      {deployment && (
        <>
          <div style={box}>
            <Checkbox
              aria-label="Attach logs"
              checked={value.logs}
              disabled={!logsOk}
              onChange={(e) => onChange({ ...value, logs: e.currentTarget.checked })}
              mt={1}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
              <span>
                <b>Logs</b> for {services}, last 60 min{' '}
                {logsOk && p.logs.counted && (
                  <span style={{ fontFamily: font.mono, fontSize: 13, color: color.textMuted }}>
                    ≈ {fileSize(p.logs.bytes)}
                  </span>
                )}
                {dedicated && preview.isFetching && (
                  <Loader size="xs" ml={6} aria-label="Loading" />
                )}
              </span>
              {dedicated && p && !p.logs.available && (
                <span style={{ fontSize: 13, color: color.textMuted }}>
                  {unavailable[p.logs.reason ?? ''] ?? 'Logs can’t be attached.'}
                </span>
              )}
              {value.logs && logsOk && p.logs.services.length > 1 && (
                <MultiSelect
                  aria-label="Services"
                  placeholder={value.services.length ? undefined : 'All services'}
                  data={p.logs.services}
                  value={value.services}
                  onChange={(v) => onChange({ ...value, services: v })}
                  clearable
                />
              )}
            </div>
          </div>
          <div style={box}>
            <Checkbox
              aria-label="Attach a health snapshot"
              checked={value.health}
              disabled={!healthOk}
              onChange={(e) => onChange({ ...value, health: e.currentTarget.checked })}
              mt={1}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>
                <b>Health snapshot</b>: services, versions, nodes right now
              </span>
              {dedicated && p && !p.health.available && (
                <span style={{ fontSize: 13, color: color.textMuted }}>
                  {unavailable[p.health.reason ?? ''] ?? 'A health snapshot can’t be attached.'}
                </span>
              )}
            </div>
          </div>
        </>
      )}
      <div style={{ ...box, display: 'block' }}>{files}</div>
      {deployment && (
        <span style={{ fontSize: 12, color: color.textMuted }}>
          Pulled from your deployment when you send.{' '}
          {dedicated && p && (logsOk || healthOk) && (
            <>
              <Anchor component="button" type="button" fz={12} onClick={() => setPreviewing(true)}>
                Preview what’s included
              </Anchor>
              .{' '}
            </>
          )}
          Not available for deployments on shared environments.
        </span>
      )}
      {preview.error ? <Alert color="red">{errorMessage(preview.error)}</Alert> : null}
      {p && (
        <PreviewDialog
          opened={previewing}
          onClose={() => setPreviewing(false)}
          preview={p}
          services={services}
        />
      )}
    </div>
  );
}

function PreviewDialog({
  opened,
  onClose,
  preview: p,
  services,
}: {
  opened: boolean;
  onClose: () => void;
  preview: AttachPreview;
  services: string;
}) {
  const snap = p.health.snapshot;
  return (
    <Modal opened={opened} onClose={onClose} title="What’s included" size="lg">
      <Stack gap={16}>
        <section>
          <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Logs</h3>
          {p.logs.available && !p.logs.counted ? (
            <Loader size="xs" aria-label="Counting log lines" />
          ) : p.logs.available ? (
            <Text size="sm">
              {p.logs.lines.toLocaleString('en-US')} lines from {services}, the last 60 minutes (
              {fileSize(p.logs.bytes)}).
              {p.logs.limited && ' Only the newest lines fit; older ones are left out.'}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              {unavailable[p.logs.reason ?? ''] ?? 'Logs can’t be attached.'}
            </Text>
          )}
        </section>
        <section>
          <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>Health snapshot</h3>
          {snap ? (
            <Stack gap={10}>
              <Text size="sm">
                {healthLabel[snap.health]}
                {snap.version && ` · ${snap.version}`} · {snap.services.length} services ·{' '}
                {snap.nodes.length} nodes
              </Text>
              <Table aria-label="Services in the snapshot" fz={13}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Service</Table.Th>
                    <Table.Th>State</Table.Th>
                    <Table.Th>Ready</Table.Th>
                    <Table.Th>Version</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {snap.services.map((s) => (
                    <Table.Tr key={s.name}>
                      <Table.Td>{s.name}</Table.Td>
                      <Table.Td>{healthLabel[s.state]}</Table.Td>
                      <Table.Td>
                        {s.ready}/{s.desired}
                      </Table.Td>
                      <Table.Td>{s.version ?? '–'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              {snap.nodes.length > 0 && (
                <Text size="sm" c="dimmed">
                  Nodes:{' '}
                  {snap.nodes.map((n) => `${n.name}${n.ready ? '' : ' (not ready)'}`).join(', ')}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              {unavailable[p.health.reason ?? ''] ?? 'A health snapshot can’t be attached.'}
            </Text>
          )}
        </section>
      </Stack>
    </Modal>
  );
}
